"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface BreedTypeSelectionProps {
  petName: string;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  setSelectedBreedType: (type: "purebred" | "crossbreed" | "mixed" | null) => void;
  setSelectedBreed: (breed: string) => void; // To clear breed when switching to mixed
  setSelectedDogSize: (size: "small" | "large" | null) => void; // To clear dog size when switching from mixed
  error: string;
  isLoading: boolean;
  groupRef: React.RefObject<HTMLDivElement>;
}

const BreedTypeSelection: React.FC<BreedTypeSelectionProps> = ({
  petName,
  selectedBreedType,
  setSelectedBreedType,
  setSelectedBreed,
  setSelectedDogSize,
  error,
  isLoading,
  groupRef,
}) => {
  const { t } = useTranslation();

  const handleBreedTypeChange = (type: "purebred" | "crossbreed" | "mixed") => {
    setSelectedBreedType(type);
    if (type === "mixed") {
      setSelectedBreed(""); // Clear breed if switching to mixed
    } else {
      setSelectedDogSize(null); // Clear dog size if switching from mixed
    }
  };

  return (
    <div className="space-y-2 field-row" ref={groupRef}>
      <Label htmlFor="breed-type">
        { petName ? t("petDetails.breedType", { petName: petName }) : t("petDetails.breedTypeUndefined") }</Label>
      <div className="flex gap-2">
        <Button
          variant={selectedBreedType === "purebred" ? "default" : "outline"}
          onClick={() => handleBreedTypeChange("purebred")}
          className={cn(
            "flex-1",
            selectedBreedType === "purebred" && "bg-primary text-primary-foreground"
          )}
          disabled={isLoading}
        >
          {t("petDetails.breedTypePurebred")}
        </Button>
        <Button
          variant={selectedBreedType === "crossbreed" ? "default" : "outline"}
          onClick={() => handleBreedTypeChange("crossbreed")}
          className={cn(
            "flex-1",
            selectedBreedType === "crossbreed" && "bg-primary text-primary-foreground"
          )}
          disabled={isLoading}
        >
          {t("petDetails.breedTypeCrossbreed")}
        </Button>
        <Button
          variant={selectedBreedType === "mixed" ? "default" : "outline"}
          onClick={() => handleBreedTypeChange("mixed")}
          className={cn(
            "flex-1",
            selectedBreedType === "mixed" && "bg-primary text-primary-foreground"
          )}
          disabled={isLoading}
        >
          {t("petDetails.breedTypeMixed")}
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default BreedTypeSelection;
